<template>
  <v-dialog :value="value" max-width="680" scrollable persistent @input="$emit('input', $event)">
    <v-card class="rounded-lg">
      <div class="analysis-dialog__header">
        <div class="analysis-dialog__heading">
          <div class="text-overline grey--text text--darken-1 analysis-dialog__overline">Pedido da Recuperanda</div>
          <div class="text-h6 font-weight-bold">{{ answer && answer.answered ? 'Alterar resposta' : 'Responder ao pedido' }}</div>
          <div v-if="answer && answer.deadline_at" class="text-caption grey--text text--darken-1">
            Prazo até {{ $moment(answer.deadline_at).format('DD/MM/YYYY') }}. Você pode alterar a resposta até a análise da Administração Judicial.
          </div>
        </div>
        <v-btn icon aria-label="Fechar" :disabled="saving" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-divider />

      <v-card-text class="pa-5">
        <div class="objection-field__label mb-1">Você concorda com o pedido da Recuperanda?</div>
        <v-radio-group v-model="form.status" class="mt-0" hide-details>
          <v-radio v-for="option in statusOptions" :key="option.value" :label="option.label" :value="option.value" />
        </v-radio-group>

        <v-text-field v-if="form.status === 3" v-model="form.amount" class="mt-4" outlined dense prefix="R$"
          label="Valor com o qual você concorda" placeholder="0,00" inputmode="decimal" hide-details="auto" />

        <v-textarea v-model="form.reason" class="mt-4" outlined auto-grow rows="4" counter="5000" maxlength="5000"
          label="Justificativa" placeholder="Explique os motivos da sua resposta." />

        <div class="objection-field__label mt-2 mb-1">Documentos (opcional)</div>
        <div v-for="doc in keptDocs" :key="doc.id" class="d-flex align-center answer-doc">
          <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
          <span class="text-body-2 flex-grow-1">{{ doc.name }}</span>
          <v-btn icon small aria-label="Remover documento" @click="removeDoc(doc)"><v-icon small>mdi-close</v-icon></v-btn>
        </div>
        <v-file-input v-model="form.files" class="mt-2" outlined dense multiple chips small-chips
          accept="application/pdf,image/jpeg,image/png" prepend-icon="" prepend-inner-icon="mdi-paperclip"
          label="Anexar documentos" hint="PDF, JPG ou PNG, até 10 MB cada, no máximo 10 documentos." persistent-hint />

        <v-alert v-if="error" type="error" dense text class="mt-4 mb-0">{{ error }}</v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text :disabled="saving" @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn color="primary" elevation="0" :loading="saving" @click="submit">
          {{ answer && answer.answered ? 'Salvar alteração' : 'Enviar resposta' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

const MAX_FILE = 10 * 1024 * 1024;

// Resposta do credor (ou do procurador) ao pedido de revisão da Recuperanda.
export default {
  name: 'CreditorAnswerDialog',
  props: {
    value: { type: Boolean, default: false },
    event: { type: Object, required: true },
    lawyer: { type: Boolean, default: false },
  },
  data: () => ({
    form: { status: null, amount: '', reason: '', files: [] },
    keptDocs: [],
    saving: false,
    error: '',
    statusOptions: [
      { value: 1, label: 'Concordo com o pedido' },
      { value: 2, label: 'Não concordo com o pedido' },
      { value: 3, label: 'Concordo parcialmente' },
    ],
  }),
  computed: {
    answer() { return this.event.creditor_answer || null; },
  },
  watch: {
    value: { immediate: true, handler(open) { if (open) this.reset(); } },
  },
  methods: {
    reset() {
      const answer = this.answer && this.answer.answered ? this.answer : null;
      this.form = {
        status: answer ? answer.status : null,
        amount: answer && answer.amount ? Number(answer.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '',
        reason: answer ? answer.reason || '' : '',
        files: [],
      };
      this.keptDocs = answer ? [...answer.docs] : [];
      this.error = '';
    },
    removeDoc(doc) { this.keptDocs = this.keptDocs.filter(item => item.id !== doc.id); },
    validate() {
      if (!this.form.status) return 'Informe se concorda, não concorda ou concorda parcialmente com o pedido.';
      if (this.form.status === 3 && !String(this.form.amount || '').trim()) return 'Informe o valor com o qual você concorda.';
      if (String(this.form.reason || '').trim().length < 10) return 'Escreva a justificativa da sua resposta (mínimo de 10 caracteres).';
      const files = this.form.files || [];
      if (files.some(file => file.size > MAX_FILE)) return 'Cada documento deve ter até 10 MB.';
      if (files.length + this.keptDocs.length > 10) return 'Envie no máximo 10 documentos.';
      return '';
    },
    async submit() {
      this.error = this.validate();
      if (this.error) return;
      const body = new FormData();
      body.append('status', this.form.status);
      if (this.form.status === 3) body.append('amount', this.form.amount);
      body.append('reason', this.form.reason);
      body.append('keep_docs', JSON.stringify(this.keptDocs.map(doc => doc.id)));
      (this.form.files || []).forEach(file => body.append('files[]', file));
      const url = this.lawyer
        ? `/api/lawyer-user-objection-events/creditor-answer/${this.event.id}`
        : `/api/user-objection-events/creditor-answer/${this.event.id}`;
      this.saving = true;
      try {
        const result = await axios.post(url, body);
        this.$emit('saved', result.data.content);
        this.$emit('input', false);
      } catch (error) {
        const content = error.response && error.response.data && error.response.data.content;
        this.error = (content && content.error && content.error.message) || 'Não foi possível enviar a resposta. Tente novamente.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style>
.answer-doc { border: 1px solid #e0e0e0; border-radius: 6px; padding: 4px 4px 4px 10px; margin-bottom: 6px; }
</style>
